/**
 * 세금계산서 회사 선택 모달 번역 상수
 * (company-list/constants.ts에서 통합됨)
 */

import type { Locale } from '@/config/locales';
import type { Language, CompanyListTranslations } from './types';

// #522 — Locale 단일 출처 정합 (15언어 전수). 누락 시 컴파일 에러.
export const companyListTranslations:
  & Record<Language, CompanyListTranslations>
  & Record<Exclude<Locale, Language>, CompanyListTranslations> = {
  ko: {
    selectCompany: '회사 선택',
    addNewCompany: '새 회사 추가',
    companyName: '회사명',
    rfcNumber: 'RFC 번호',
    address: '주소',
    email: '이메일 (선택사항)',
    save: '저장',
    cancel: '취소',
    back: '뒤로',
    selectForInvoice: '세금계산서 발급용 회사를 선택하세요',
    addCompanyTitle: '새 회사 정보',
    rfcRequired: 'RFC 번호는 필수입니다',
    companyRequired: '회사명은 필수입니다',
    addressRequired: '주소는 필수입니다',
    defaultCompany: '기본 회사'
  },
  es: {
    selectCompany: 'Seleccionar Empresa',
    addNewCompany: 'Agregar Nueva Empresa',
    companyName: 'Nombre de la empresa',
    rfcNumber: 'RFC',
    address: 'Dirección',
    email: 'Email (opcional)',
    save: 'Guardar',
    cancel: 'Cancelar',
    back: 'Atrás',
    selectForInvoice: 'Seleccione la empresa para la factura fiscal',
    addCompanyTitle: 'Información de Nueva Empresa',
    rfcRequired: 'El RFC es obligatorio',
    companyRequired: 'El nombre de la empresa es obligatorio',
    addressRequired: 'La dirección es obligatoria',
    defaultCompany: 'Empresa predeterminada'
  },
  en: {
    selectCompany: 'Select Company',
    addNewCompany: 'Add New Company',
    companyName: 'Company name',
    rfcNumber: 'RFC Number',
    address: 'Address',
    email: 'Email (optional)',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    selectForInvoice: 'Select company for tax invoice',
    addCompanyTitle: 'New Company Information',
    rfcRequired: 'RFC number is required',
    companyRequired: 'Company name is required',
    addressRequired: 'Address is required',
    defaultCompany: 'Default company'
  },
  pt: {
    selectCompany: 'Selecionar empresa',
    addNewCompany: 'Adicionar nova empresa',
    companyName: 'Nome da empresa',
    rfcNumber: 'Número de RFC',
    address: 'Endereço',
    email: 'E-mail (opcional)',
    save: 'Salvar',
    cancel: 'Cancelar',
    back: 'Voltar',
    selectForInvoice: 'Selecione a empresa para a fatura fiscal',
    addCompanyTitle: 'Informações da nova empresa',
    rfcRequired: 'O número de RFC é obrigatório',
    companyRequired: 'O nome da empresa é obrigatório',
    addressRequired: 'O endereço é obrigatório',
    defaultCompany: 'Empresa padrão'
  },
  fr: {
    selectCompany: 'Sélectionner une entreprise',
    addNewCompany: 'Ajouter une nouvelle entreprise',
    companyName: "Nom de l'entreprise",
    rfcNumber: 'Numéro RFC',
    address: 'Adresse',
    email: 'E-mail (facultatif)',
    save: 'Enregistrer',
    cancel: 'Annuler',
    back: 'Retour',
    selectForInvoice: "Sélectionnez l'entreprise pour la facture fiscale",
    addCompanyTitle: 'Informations sur la nouvelle entreprise',
    rfcRequired: 'Le numéro RFC est obligatoire',
    companyRequired: "Le nom de l'entreprise est obligatoire",
    addressRequired: "L'adresse est obligatoire",
    defaultCompany: 'Entreprise par défaut'
  },
  zh: {
    selectCompany: '选择公司',
    addNewCompany: '添加新公司',
    companyName: '公司名称',
    rfcNumber: 'RFC 编号',
    address: '地址',
    email: '电子邮箱（可选）',
    save: '保存',
    cancel: '取消',
    back: '返回',
    selectForInvoice: '请选择用于开具税务发票的公司',
    addCompanyTitle: '新公司信息',
    rfcRequired: 'RFC 编号为必填项',
    companyRequired: '公司名称为必填项',
    addressRequired: '地址为必填项',
    defaultCompany: '默认公司'
  },
  ja: {
    selectCompany: '会社を選択',
    addNewCompany: '新しい会社を追加',
    companyName: '会社名',
    rfcNumber: 'RFC番号',
    address: '住所',
    email: 'メールアドレス（任意）',
    save: '保存',
    cancel: 'キャンセル',
    back: '戻る',
    selectForInvoice: '税務インボイス発行用の会社を選択してください',
    addCompanyTitle: '新しい会社情報',
    rfcRequired: 'RFC番号は必須です',
    companyRequired: '会社名は必須です',
    addressRequired: '住所は必須です',
    defaultCompany: 'デフォルトの会社'
  },
  hi: {
    selectCompany: 'कंपनी चुनें',
    addNewCompany: 'नई कंपनी जोड़ें',
    companyName: 'कंपनी का नाम',
    rfcNumber: 'RFC नंबर',
    address: 'पता',
    email: 'ईमेल (वैकल्पिक)',
    save: 'सहेजें',
    cancel: 'रद्द करें',
    back: 'वापस',
    selectForInvoice: 'कर चालान जारी करने के लिए कंपनी चुनें',
    addCompanyTitle: 'नई कंपनी की जानकारी',
    rfcRequired: 'RFC नंबर आवश्यक है',
    companyRequired: 'कंपनी का नाम आवश्यक है',
    addressRequired: 'पता आवश्यक है',
    defaultCompany: 'डिफ़ॉल्ट कंपनी'
  },
  de: {
    selectCompany: 'Firma auswählen',
    addNewCompany: 'Neue Firma hinzufügen',
    companyName: 'Firmenname',
    rfcNumber: 'RFC-Nummer',
    address: 'Adresse',
    email: 'E-Mail (optional)',
    save: 'Speichern',
    cancel: 'Abbrechen',
    back: 'Zurück',
    selectForInvoice: 'Wählen Sie die Firma für die Steuerrechnung',
    addCompanyTitle: 'Angaben zur neuen Firma',
    rfcRequired: 'Die RFC-Nummer ist erforderlich',
    companyRequired: 'Der Firmenname ist erforderlich',
    addressRequired: 'Die Adresse ist erforderlich',
    defaultCompany: 'Standardfirma'
  },
  it: {
    selectCompany: 'Seleziona azienda',
    addNewCompany: 'Aggiungi nuova azienda',
    companyName: "Nome dell'azienda",
    rfcNumber: 'Numero RFC',
    address: 'Indirizzo',
    email: 'Email (facoltativa)',
    save: 'Salva',
    cancel: 'Annulla',
    back: 'Indietro',
    selectForInvoice: "Seleziona l'azienda per la fattura fiscale",
    addCompanyTitle: 'Informazioni sulla nuova azienda',
    rfcRequired: 'Il numero RFC è obbligatorio',
    companyRequired: "Il nome dell'azienda è obbligatorio",
    addressRequired: "L'indirizzo è obbligatorio",
    defaultCompany: 'Azienda predefinita'
  },
  ru: {
    selectCompany: 'Выбрать компанию',
    addNewCompany: 'Добавить новую компанию',
    companyName: 'Название компании',
    rfcNumber: 'Номер RFC',
    address: 'Адрес',
    email: 'Эл. почта (необязательно)',
    save: 'Сохранить',
    cancel: 'Отмена',
    back: 'Назад',
    selectForInvoice: 'Выберите компанию для налогового счёта',
    addCompanyTitle: 'Данные новой компании',
    rfcRequired: 'Номер RFC обязателен',
    companyRequired: 'Название компании обязательно',
    addressRequired: 'Адрес обязателен',
    defaultCompany: 'Компания по умолчанию'
  },
  ar: {
    selectCompany: 'اختيار الشركة',
    addNewCompany: 'إضافة شركة جديدة',
    companyName: 'اسم الشركة',
    rfcNumber: 'رقم RFC',
    address: 'العنوان',
    email: 'البريد الإلكتروني (اختياري)',
    save: 'حفظ',
    cancel: 'إلغاء',
    back: 'رجوع',
    selectForInvoice: 'اختر الشركة لإصدار الفاتورة الضريبية',
    addCompanyTitle: 'معلومات الشركة الجديدة',
    rfcRequired: 'رقم RFC مطلوب',
    companyRequired: 'اسم الشركة مطلوب',
    addressRequired: 'العنوان مطلوب',
    defaultCompany: 'الشركة الافتراضية'
  },
  vi: {
    selectCompany: 'Chọn công ty',
    addNewCompany: 'Thêm công ty mới',
    companyName: 'Tên công ty',
    rfcNumber: 'Số RFC',
    address: 'Địa chỉ',
    email: 'Email (không bắt buộc)',
    save: 'Lưu',
    cancel: 'Hủy',
    back: 'Quay lại',
    selectForInvoice: 'Chọn công ty để xuất hóa đơn thuế',
    addCompanyTitle: 'Thông tin công ty mới',
    rfcRequired: 'Số RFC là bắt buộc',
    companyRequired: 'Tên công ty là bắt buộc',
    addressRequired: 'Địa chỉ là bắt buộc',
    defaultCompany: 'Công ty mặc định'
  },
  th: {
    selectCompany: 'เลือกบริษัท',
    addNewCompany: 'เพิ่มบริษัทใหม่',
    companyName: 'ชื่อบริษัท',
    rfcNumber: 'หมายเลข RFC',
    address: 'ที่อยู่',
    email: 'อีเมล (ไม่บังคับ)',
    save: 'บันทึก',
    cancel: 'ยกเลิก',
    back: 'ย้อนกลับ',
    selectForInvoice: 'เลือกบริษัทสำหรับออกใบกำกับภาษี',
    addCompanyTitle: 'ข้อมูลบริษัทใหม่',
    rfcRequired: 'จำเป็นต้องระบุหมายเลข RFC',
    companyRequired: 'จำเป็นต้องระบุชื่อบริษัท',
    addressRequired: 'จำเป็นต้องระบุที่อยู่',
    defaultCompany: 'บริษัทเริ่มต้น'
  },
  nl: {
    selectCompany: 'Bedrijf selecteren',
    addNewCompany: 'Nieuw bedrijf toevoegen',
    companyName: 'Bedrijfsnaam',
    rfcNumber: 'RFC-nummer',
    address: 'Adres',
    email: 'E-mail (optioneel)',
    save: 'Opslaan',
    cancel: 'Annuleren',
    back: 'Terug',
    selectForInvoice: 'Selecteer het bedrijf voor de belastingfactuur',
    addCompanyTitle: 'Gegevens nieuw bedrijf',
    rfcRequired: 'Het RFC-nummer is verplicht',
    companyRequired: 'De bedrijfsnaam is verplicht',
    addressRequired: 'Het adres is verplicht',
    defaultCompany: 'Standaardbedrijf'
  }
};
